import { ActivityCardData } from '../components/card-deck/types';
import { BackendActivity } from '../types/activity';
import type { RecommendedActivity } from '../types/recommendation.types';
import type { Child } from '../types/assessment.types';
import { getChildAvatarSrc } from './childAvatars';

// Import placeholder assets
import childSmilingImg from '../../assets/images/default-activity.jpeg';

/**
 * Asset paths for placeholder values
 */
const PLACEHOLDER_ASSETS = {
  childSmiling: childSmilingImg,
};

/**
 * Transform backend Activity to frontend ActivityCardData
 * Backend now provides signed S3 URLs in imageUrl field
 *
 * @param activity - Backend activity from API
 * @returns Frontend card data ready for display
 */
export function transformActivityToCard(activity: BackendActivity): ActivityCardData {
  return {
    type: 'activity',
    id: activity.id,
    title: activity.title,
    description: activity.description,
    imageUrl: activity.imageUrl || PLACEHOLDER_ASSETS.childSmiling,  // Use backend URL or fallback
    likesCount: activity?.statistics?.thumbsUpCount ?? 0, // Use real thumbsUpCount from backend
    metadata: {
      duration: activity.durationText,      // Already formatted by backend
      playType: activity.playTypeText,      // Already formatted by backend
      prepNeeded: activity.prepNeededText,  // Already formatted by backend
    },
  };
}

/**
 * Transform a RecommendedActivity to frontend ActivityCardData.
 * Includes recommendation metadata when the activity is an engine recommendation.
 *
 * @param activity - Recommended activity from API (may have recommendedFor !== null)
 * @param child - Child data for recommendation display (name + avatar)
 * @param skillCategory - Category name for the "Strengthens X Skills" label
 * @returns Frontend card data with optional recommendation section
 */
export function transformRecommendedActivityToCard(
  activity: RecommendedActivity,
  child: Child | null,
  //skillCategory?: string
): ActivityCardData {
  const isEngineRecommendation = activity.recommendedFor !== null && child !== null;

  const skill = activity?.tags?.slice(0, 3)?.map((tag) => tag.label)?.join(', ') ?? 'Development'; // Fallback skill category if tags are missing

  return {
    type: 'activity',
    id: activity.id,
    title: activity.title,
    description: activity.description,
    imageUrl: activity.imageUrl || PLACEHOLDER_ASSETS.childSmiling,
    likesCount: activity?.statistics?.thumbsUpCount ?? 0,
    metadata: {
      duration: activity.durationText,
      playType: activity.playTypeText,
      prepNeeded: activity.prepNeededText,
    },
    whyItWorks: activity.whyItWorks ? { text: activity.whyItWorks.text } : null,
    categoryColor: activity.categoryColor ?? null,
    recommendation: isEngineRecommendation
      ? {
          childName: child!.nickname,
          childAvatarUrl: getChildAvatarSrc(child!.avatarKey),
          skillCategory: skill,
        }
      : undefined,
  };
}

/**
 * Transform array of backend activities to card data
 *
 * @param activities - Array of backend activities
 * @returns Array of frontend card data
 */
export function transformActivitiesToCards(activities: BackendActivity[]): ActivityCardData[] {
  return activities.map(transformActivityToCard);
}

/**
 * Transform array of recommended activities to card data.
 * Includes recommendation section for engine recommendations.
 *
 * @param activities - Array of recommended activities from API
 * @param child - First child (v1: single child selection)
 * @returns Array of frontend card data with recommendation metadata
 */
export function transformRecommendedActivitiesToCards(
  activities: RecommendedActivity[],
  child: Child | null
): ActivityCardData[] {
  return activities.map((activity) =>
    transformRecommendedActivityToCard(activity, child)
  );
}
